"use client"

import { FacebookBrand, InstagramBrand, WhatsAppBrand, MetaBrand, Handshake, HandManual } from "@/lib/icons"

/**
 * Catálogo de fuentes de lead.
 * Cada source tiene un icono real (Font Awesome / Phosphor) + color de marca.
 * - landing: usa el logo del Instituto Tepeyac (img custom desde /it-logo.webp)
 * - meta_ads: Logo Meta oficial (∞ infinity loop, azul Meta #0866FF)
 * - facebook: Facebook brand icon (azul Facebook)
 * - instagram_ads: Instagram brand icon (gradiente)
 * - whatsapp_directo: WhatsApp brand icon (verde)
 * - recomendacion: handshake (ámbar)
 * - manual: hand wave (gris) — registrado por una asesora desde el CRM
 */
export const SOURCE_META = {
  landing: {
    label: "Landing Page",
    type: "image",
    image: "/logo-tepeyac.png", // Triángulo solo (igual que el favicon de la landing)
    bg: "#ffffff",
    border: "#7a1b33",
  },
  meta_ads: {
    label: "Meta Ads",
    type: "icon",
    icon: MetaBrand,
    bg: "#0866FF",
    color: "#ffffff",
  },
  facebook: {
    label: "Facebook",
    type: "icon",
    icon: FacebookBrand,
    bg: "#1877F2",
    color: "#ffffff",
  },
  instagram_ads: {
    label: "Instagram Ads",
    type: "icon",
    icon: InstagramBrand,
    bg: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    color: "#ffffff",
  },
  facebook_ads: {
    label: "Facebook Ads",
    type: "icon",
    icon: FacebookBrand,
    bg: "#1877F2",
    color: "#ffffff",
  },
  instagram: {
    label: "Instagram",
    type: "icon",
    icon: InstagramBrand,
    bg: "linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
    color: "#ffffff",
  },
  whatsapp_directo: {
    label: "WhatsApp",
    type: "icon",
    icon: WhatsAppBrand,
    bg: "#25D366",
    color: "#ffffff",
  },
  whatsapp: {
    label: "WhatsApp",
    type: "icon",
    icon: WhatsAppBrand,
    bg: "#25D366",
    color: "#ffffff",
  },
  recomendacion: {
    label: "Recomendación",
    type: "icon",
    icon: Handshake,
    bg: "#f59e0b",
    color: "#ffffff",
  },
  manual: {
    label: "Manual (CRM)",
    type: "icon",
    icon: HandManual,
    bg: "#64748b",
    color: "#ffffff",
  },
}

/**
 * Catálogo de detail labels (para tooltip y vistas verbose).
 */
export const SOURCE_DETAIL_LABELS = {
  landing_preescolar: "Maternal / Kinder / Pre-First",
  landing_primaria: "Primaria",
  landing_secundaria: "Secundaria",
  landing_preparatoria: "Preparatoria",
  landing_cita: "Agendar cita",
  landing_visita: "Agendar visita",
  landing_asesor: "Hablar con asesor",
  landing_campus_gdl: "Campus Guadalajara",
  landing_general: "Información general",
  meta_ads_preescolar: "Maternal / Kinder / Pre-First",
  meta_ads_primaria: "Primaria",
  meta_ads_secundaria: "Secundaria",
  meta_ads_preparatoria: "Preparatoria",
  meta_ads_general: "Información general",
}

/**
 * Resuelve el meta de una fuente. Si no existe, devuelve el de "manual".
 */
export function getSourceMeta(source) {
  if (!source) return SOURCE_META.manual
  const key = String(source).toLowerCase().trim()
  return SOURCE_META[key] || SOURCE_META.manual
}

const APP_LABELS = { instagram: "Instagram", facebook: "Facebook", meta: "Meta" }

/**
 * Convierte un source_detail desconocido (p.ej. meta_ads_instagram_agendar_cita,
 * generado con el anuncio real del webhook) en texto legible: "Instagram · Agendar Cita".
 * Los detalles conocidos del catálogo SOURCE_DETAIL_LABELS tienen prioridad.
 */
export function prettifySourceDetail(detail) {
  if (!detail) return null
  if (SOURCE_DETAIL_LABELS[detail]) return SOURCE_DETAIL_LABELS[detail]
  let rest = String(detail).replace(/^(meta_ads|landing|fb_ad)_/i, "")
  const appMatch = rest.match(/^(instagram|facebook|meta)_?/i)
  let app = null
  if (appMatch) {
    app = APP_LABELS[appMatch[1].toLowerCase()]
    rest = rest.slice(appMatch[0].length)
  }
  const title = rest
    .replace(/_/g, " ")
    .trim()
    .replace(/(^|\s)\p{L}/gu, (c) => c.toUpperCase())
  if (app && title) return `${app} · ${title}`
  return app || title || null
}

/**
 * Construye la línea de detalle preferida: si hay evidencia del webhook
 * (source_metadata.ad_title), usa el anuncio real; si no, el source_detail.
 */
export function getSourceDetailLabel(sourceDetail, sourceMetadata) {
  const sm = sourceMetadata || {}
  if (sm.ad_title) {
    const app = sm.app ? APP_LABELS[String(sm.app).toLowerCase()] : null
    return app ? `${app} · ${sm.ad_title}` : sm.ad_title
  }
  return prettifySourceDetail(sourceDetail)
}

/**
 * Construye el tooltip completo: "Meta Ads → Instagram · AGENDAR CITA"
 */
export function getSourceTooltip(source, sourceDetail, sourceMetadata) {
  const meta = getSourceMeta(source)
  const detail = getSourceDetailLabel(sourceDetail, sourceMetadata)
  return detail ? `${meta.label} → ${detail}` : meta.label
}

/**
 * Badge visual de fuente.
 *
 * @param {string} source - El valor de leads.source (landing, meta_ads, etc)
 * @param {string} sourceDetail - El valor de leads.source_detail (landing_kinder, etc)
 * @param {number} size - Tamaño del badge en px (default 16). Solo aplica a vista circular.
 * @param {string} className - Clases extra
 * @param {boolean} ringed - Si lleva ring blanco para destacarse sobre fondos
 * @param {boolean} expanded - Si es true, renderiza pill desplegada con ícono + label (estilo tabla)
 */
export function LeadSourceBadge({
  source,
  sourceDetail,
  size = 16,
  className = "",
  ringed = true,
  expanded = false,
}) {
  if (!source) return null
  const meta = getSourceMeta(source)
  const tooltip = getSourceTooltip(source, sourceDetail)
  const ringClass = ringed ? "ring-2 ring-white dark:ring-slate-900" : ""

  // Vista expandida: pill horizontal con ícono + texto
  if (expanded) {
    const iconSize = 10
    const circleSize = 14
    return (
      <span
        title={tooltip}
        className={`inline-flex items-center gap-1 ${className}`}
      >
        {meta.type === "image" ? (
          <span
            className="inline-flex items-center justify-center rounded-full overflow-hidden flex-shrink-0"
            style={{
              width: circleSize,
              height: circleSize,
              backgroundColor: meta.bg,
              border: `1px solid ${meta.border}`,
            }}
          >
            <img
              src={meta.image}
              alt=""
              className="object-contain"
              style={{ width: 10, height: 10 }}
            />
          </span>
        ) : (
          <span
            className="inline-flex items-center justify-center rounded-full flex-shrink-0"
            style={{
              width: circleSize,
              height: circleSize,
              background: meta.bg,
              color: meta.color,
            }}
          >
            {(() => { const Icon = meta.icon; return <Icon style={{ width: iconSize, height: iconSize }} /> })()}
          </span>
        )}
        <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300 leading-none">
          {meta.label}
        </span>
      </span>
    )
  }

  // Vista circular (default)
  if (meta.type === "image") {
    return (
      <span
        title={tooltip}
        className={`inline-flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 shadow-sm ${ringClass} ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: meta.bg,
          border: `1.5px solid ${meta.border}`,
        }}
      >
        <img
          src={meta.image}
          alt={meta.label}
          className="object-contain"
          style={{ width: size * 0.78, height: size * 0.78 }}
        />
      </span>
    )
  }

  const Icon = meta.icon
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center justify-center rounded-full flex-shrink-0 shadow-sm ${ringClass} ${className}`}
      style={{
        width: size,
        height: size,
        background: meta.bg,
        color: meta.color,
      }}
    >
      <Icon style={{ width: size * 0.62, height: size * 0.62 }} />
    </span>
  )
}
